import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CapabilitiesPage — the apps × capabilities matrix (NEXUS-21 P2).
 *
 * The multiselect the legacy K-Stack Gateway had as applications.allowed_models
 * and Nexus lost. Talks to GET/PUT /v1/admin/capability-access.
 * Design: Nexus/Docs/PLAN_FEATURE_ACCESS_TIERS.md.
 *
 * Two rules this page exists to make visible:
 *   - Capabilities the INSTANCE never deployed get no column at all. Showing a
 *     toggle that cannot take effect is how an operator ends up filing a bug
 *     against their own configuration.
 *   - `suspended` is billing-owned and renders read-only. An operator who
 *     could clear one by clicking could hand out unpaid capability.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
function formatError(e) {
    if (e instanceof NexusAdminApiError)
        return e.message;
    if (e instanceof Error)
        return e.message;
    return String(e);
}
/** appId + ' ' + capability — neither part can contain a space. */
const cellKey = (appId, cap) => `${appId} ${cap}`;
export function CapabilitiesPage() {
    const fetcher = useNexusFetch();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [reason, setReason] = useState('');
    /** Pending edits, keyed by cell. Only these are sent on save. */
    const [dirty, setDirty] = useState(new Map());
    const load = useCallback(async () => {
        setErr(null);
        try {
            const d = await fetcher('/v1/admin/capability-access');
            setData(d);
            setDirty(new Map());
        }
        catch (e) {
            setErr(formatError(e));
        }
    }, [fetcher]);
    useEffect(() => { void load(); }, [load]);
    /** Only capabilities live on THIS instance get a column. */
    const columns = useMemo(() => (data ? data.registry.filter((r) => data.instance[r.key] === true) : []), [data]);
    const hidden = useMemo(() => (data ? data.registry.filter((r) => data.instance[r.key] !== true) : []), [data]);
    const cellOf = (app, cap) => app.capabilities.find((c) => c.capability === cap);
    const stateOf = (app, cap) => {
        const pending = dirty.get(cellKey(app.appId, cap));
        if (pending)
            return pending;
        return cellOf(app, cap)?.state ?? 'disabled';
    };
    const toggle = (app, cap) => {
        const cell = cellOf(app, cap);
        if (cell?.state === 'suspended')
            return; // billing owns it
        const next = stateOf(app, cap) === 'enabled' ? 'disabled' : 'enabled';
        setDirty((prev) => {
            const m = new Map(prev);
            const k = cellKey(app.appId, cap);
            // Toggling back to where it started is not an edit.
            if (cell && cell.state === next)
                m.delete(k);
            else
                m.set(k, next);
            return m;
        });
    };
    const setColumn = (cap, state) => {
        if (!data)
            return;
        setDirty((prev) => {
            const m = new Map(prev);
            for (const app of data.apps) {
                const cell = cellOf(app, cap);
                if (cell?.state === 'suspended')
                    continue;
                const k = cellKey(app.appId, cap);
                if (cell && cell.state === state)
                    m.delete(k);
                else
                    m.set(k, state);
            }
            return m;
        });
    };
    const save = async () => {
        if (!data || dirty.size === 0)
            return;
        setSaving(true);
        setErr(null);
        const updates = Array.from(dirty.entries()).map(([k, state]) => {
            const [appId, capability] = k.split(' ');
            return { appId, capability, state, ...(reason.trim() ? { reason: reason.trim() } : {}) };
        });
        try {
            const res = await fetcher('/v1/admin/capability-access', {
                method: 'PUT',
                json: { updates },
            });
            setSavedAt(`${res.updated} change${res.updated === 1 ? '' : 's'} applied`);
            setTimeout(() => setSavedAt(null), 3000);
            setReason('');
            await load(); // re-read; never assume
        }
        catch (e) {
            // A partial write reports how far it got, so re-reading is the only
            // honest way to show what actually landed.
            setErr(formatError(e));
            await load();
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsxs("div", { className: "container-fluid", children: [_jsx("h3", { className: "mb-1", children: "Capabilities" }), _jsxs("p", { className: "text-muted", children: ["Which features each app may use \u2014 the ", _jsx("strong", { children: "app tier" }), " of feature access. Access narrows: a capability must be enabled on this ", _jsx("strong", { children: "instance" }), ", then for the ", _jsx("strong", { children: "app" }), ", then (soon) for the ", _jsx("strong", { children: "user" }), ". A tier can only take access away, never grant past the one above it."] }), err ? _jsx("div", { className: "alert alert-warning", children: err }) : null, savedAt ? _jsx("div", { className: "alert alert-success py-2", children: savedAt }) : null, !data ? _jsx("div", { className: "text-muted", children: "Loading\u2026" }) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "card mb-3", children: _jsx("div", { className: "card-body p-0", children: _jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm table-hover align-middle mb-0", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { style: { minWidth: 200 }, children: "App" }), columns.map((c) => (_jsxs("th", { className: "text-center", title: c.description, children: [_jsx("div", { children: c.displayName }), _jsxs("div", { className: "btn-group btn-group-sm mt-1", role: "group", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary py-0", onClick: () => setColumn(c.key, 'enabled'), children: "all on" }), _jsx("button", { type: "button", className: "btn btn-outline-secondary py-0", onClick: () => setColumn(c.key, 'disabled'), children: "off" })] })] }, c.key)))] }) }), _jsx("tbody", { children: data.apps.map((app) => (_jsxs("tr", { children: [_jsxs("td", { children: [_jsx("div", { children: _jsx("strong", { children: app.displayName }) }), _jsxs("div", { className: "text-muted small", children: [app.appId, app.status !== 'active' ? ` · ${app.status}` : ''] })] }), columns.map((c) => {
                                                        const cell = cellOf(app, c.key);
                                                        const state = stateOf(app, c.key);
                                                        const isDirty = dirty.has(cellKey(app.appId, c.key));
                                                        if (cell?.state === 'suspended') {
                                                            return (_jsx("td", { className: "text-center", children: _jsx("span", { className: "badge bg-warning text-dark", title: cell.reason ?? 'Held by billing', children: "held by billing" }) }, c.key));
                                                        }
                                                        return (_jsxs("td", { className: "text-center", children: [_jsx("div", { className: "form-check form-switch d-inline-block", children: _jsx("input", { className: "form-check-input", type: "checkbox", role: "switch", checked: state === 'enabled', onChange: () => toggle(app, c.key), "aria-label": `${c.displayName} for ${app.appId}` }) }), _jsx("div", { className: "small text-muted", style: { lineHeight: 1.1 }, children: isDirty ? _jsx("span", { className: "text-primary", children: "unsaved" })
                                                                        : cell?.explicit ? (cell.source ?? 'set')
                                                                            : _jsx("em", { children: "default" }) })] }, c.key));
                                                    })] }, app.appId))) })] }) }) }) }), _jsxs("div", { className: "d-flex align-items-center gap-2 mb-3 flex-wrap", children: [_jsx("input", { className: "form-control", style: { maxWidth: 380 }, placeholder: "Reason (optional) \u2014 shown in the audit trail", value: reason, onChange: (e) => setReason(e.target.value) }), _jsx("button", { className: "btn btn-primary", disabled: dirty.size === 0 || saving, onClick: () => void save(), children: saving ? 'Saving…' : `Save ${dirty.size || ''} change${dirty.size === 1 ? '' : 's'}` }), _jsx("button", { className: "btn btn-outline-secondary", disabled: dirty.size === 0 || saving, onClick: () => setDirty(new Map()), children: "Discard" })] }), hidden.length > 0 ? (_jsxs("div", { className: "card", children: [_jsx("div", { className: "card-header", children: _jsx("strong", { children: "Not deployed on this instance" }) }), _jsxs("div", { className: "card-body py-2", children: [_jsx("p", { className: "text-muted small mb-2", children: "These capabilities have no column because this deployment was not built with them. Turning one on is a config change plus a redeploy, not a toggle here \u2014 instance-tier flags stay in git so production changes stay reviewable." }), _jsx("ul", { className: "mb-0 small", children: hidden.map((c) => (_jsxs("li", { children: [_jsx("strong", { children: c.displayName }), " \u2014 ", c.description, c.module ? _jsxs(_Fragment, { children: [" ", _jsxs("span", { className: "text-muted", children: ["(module ", _jsx("code", { children: c.module }), ")"] })] }) : null] }, c.key))) })] })] })) : null] }))] }));
}
//# sourceMappingURL=CapabilitiesPage.js.map